// @ts-nocheck
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Loader from './Loader';
import { getSessionUserEmail } from '../config/common';
import * as upgradeRequestAction from '../Action/Upgrade_Request/Upgrade_Request_Action';

/**
 * "Request Upgrade" dialog, shared by the Change Log and Upgrade Requests screens.
 *
 * Extracted from ReleaseNotes rather than copied: this is the only way a customer files an upgrade
 * request, and two copies of the form would drift — a validation rule or field added on one screen
 * and quietly missing on the other. The markup, classes, field ids and copy are unchanged from the
 * Change Log original so it stays visually identical.
 *
 * Props:
 *   show      — whether the dialog is open
 *   versions  — [{ fixVersion, name, versionNumber }] for the Version dropdown
 *   onClose   — called on cancel/close
 *   onCreated — called after a successful submit, so the caller can refresh its list
 */
function RequestUpgradeModal({ show, versions = [], onClose, onCreated }) {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [requestForm, setRequestForm] = useState({
        SBRequestedDate: '',
        TargetVersion: '',
        ProdRequestedDate: '',
        Comments: ''
    });

    if (!show) return null;

    const handleInputChange = (e) => {
        setRequestForm({ ...requestForm, [e.target.name]: e.target.value });
    };

    const createRequest = () => {
        var param = { ...requestForm };
        param.SBRequestedDate = param.SBRequestedDate || null;
        param.ProdRequestedDate = param.ProdRequestedDate || null;
        if (!param.TargetVersion) {
            toast.error("Please select version.");
            return;
        }
        if (!param.Comments) {
            toast.error("Please enter comments.");
            return;
        }
        param.email = getSessionUserEmail();
        setIsLoading(true);
        dispatch(upgradeRequestAction.upgrade_request({ data: JSON.stringify(param) })).then((response) => {
            setIsLoading(false);
            if (response.status !== 1) {
                toast.error(response.message);
                return;
            }
            toast.success("Upgrade request submitted successfully.");
            setRequestForm({ SBRequestedDate: '', TargetVersion: '', ProdRequestedDate: '', Comments: '' });
            onClose && onClose();
            onCreated && onCreated();
        }).catch(error => {
            setIsLoading(false);
            toast.error(error.message);
        })
    };

    return (
        <>
            {isLoading && <Loader />}
            <div
                className="modal fade show copilot-changelog-upgrade-modal-root"
                id="createRequestModal"
                role="dialog"
                aria-labelledby="createRequestModalLabel"
                aria-modal="true"
                aria-hidden="false"
                style={{ display: 'block' }}
            >
                <div className="modal-dialog modal-dialog-centered mw-570 px-2" role="document">
                    <div className="modal-content copilot-changelog-upgrade-modal">
                        <div className="copilot-changelog-upgrade-modal__accent" aria-hidden />
                        <div className="modal-header copilot-changelog-upgrade-modal__header border-0">
                            <div className="flex-grow-1 min-w-0 pe-2">
                                <h2
                                    className="copilot-changelog-section-title copilot-changelog-upgrade-modal__title"
                                    id="createRequestModalLabel"
                                >
                                    Request Upgrade
                                </h2>
                                <p className="copilot-changelog-section-lead copilot-changelog-upgrade-modal__lead mb-0 mt-1">
                                    Version and comments are required. Dates are optional.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn-close copilot-changelog-upgrade-modal__close flex-shrink-0 mt-0"
                                aria-label="Close"
                                onClick={onClose}
                            />
                        </div>
                        <div className="modal-body copilot-changelog-upgrade-modal__body">
                            <form
                                id="changelog-upgrade-request-form"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    createRequest();
                                }}
                            >
                                <div className="copilot-changelog-upgrade-modal__fields">
                                    <div className="row g-2">
                                        <div className="col-12">
                                            <label
                                                className="copilot-changelog-upgrade-modal__label"
                                                htmlFor="changelog-upgrade-version"
                                            >
                                                Version
                                            </label>
                                            <select
                                                id="changelog-upgrade-version"
                                                className="form-select form-select-sm copilot-changelog-upgrade-modal__control rounded-3"
                                                name="TargetVersion"
                                                value={requestForm.TargetVersion}
                                                onChange={(e) => handleInputChange(e)}
                                            >
                                                <option value="">Select version</option>
                                                {versions.map((version, index) => (
                                                    <option key={index} value={version.fixVersion}>
                                                        {version.name} —{' '}
                                                        {version.versionNumber}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-sm-6">
                                            <label
                                                className="copilot-changelog-upgrade-modal__label"
                                                htmlFor="changelog-sb-date"
                                            >
                                                SB requested date
                                            </label>
                                            <input
                                                id="changelog-sb-date"
                                                type="date"
                                                className="form-control form-control-sm login-form-control copilot-changelog-upgrade-modal__control rounded-3"
                                                name="SBRequestedDate"
                                                value={requestForm.SBRequestedDate}
                                                onChange={(e) => handleInputChange(e)}
                                            />
                                        </div>
                                        <div className="col-sm-6">
                                            <label
                                                className="copilot-changelog-upgrade-modal__label"
                                                htmlFor="changelog-prod-date"
                                            >
                                                Prod requested date
                                            </label>
                                            <input
                                                id="changelog-prod-date"
                                                type="date"
                                                className="form-control form-control-sm login-form-control copilot-changelog-upgrade-modal__control rounded-3"
                                                name="ProdRequestedDate"
                                                value={requestForm.ProdRequestedDate}
                                                onChange={(e) => handleInputChange(e)}
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label
                                                className="copilot-changelog-upgrade-modal__label"
                                                htmlFor="changelog-comments"
                                            >
                                                Comments
                                            </label>
                                            <textarea
                                                id="changelog-comments"
                                                name="Comments"
                                                value={requestForm.Comments}
                                                onChange={(e) => handleInputChange(e)}
                                                className="form-control form-control-sm login-form-control copilot-changelog-upgrade-modal__control copilot-changelog-upgrade-modal__textarea rounded-3"
                                                rows={3}
                                                placeholder="Brief context for your request."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer copilot-changelog-upgrade-modal__footer border-0 gap-2">
                            <button
                                className="btn btn-sm btn-outline-secondary login-form-secondary"
                                type="button"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="changelog-upgrade-request-form"
                                className="btn btn-sm btn-primary login-form-submit"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show copilot-changelog-upgrade-modal-backdrop" />
        </>
    );
}

export default RequestUpgradeModal;
